'use client';

import React, { useEffect, useRef } from 'react';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import animationData from '@/public/arbi.json';
import { Button } from './Button';

interface ValidandoPlanilhaScreenProps {
  title?: string;
  subtitle?: string;
  processed?: number;
  total?: number;
  onCancelar?: () => void;
}

export function ValidandoPlanilhaScreen({
  title = 'Validando planilha...',
  subtitle = 'Aguarde enquanto processamos os dados',
  processed,
  total,
  onCancelar,
}: ValidandoPlanilhaScreenProps) {
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  useEffect(() => {
    if (lottieRef.current) {
      lottieRef.current.setSpeed(1.8);
    }
  }, []);

  const hasTotal = typeof total === 'number' && total > 0;
  const safeTotal = hasTotal ? total! : 0;
  const safeProcessed =
    hasTotal && typeof processed === 'number' ? Math.max(0, Math.min(processed, safeTotal)) : 0;
  const percent = hasTotal ? Math.min(100, Math.round((safeProcessed / safeTotal) * 100)) : 0;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white">
      <div className="relative flex flex-col items-center">
        <div className="h-48 w-48">
          <Lottie
            lottieRef={lottieRef}
            animationData={animationData}
            loop={true}
            autoplay={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        <div className="mt-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <p className="mt-2 text-sm text-gray-600">{subtitle}</p>

          <div className="mt-6 flex w-72 flex-col items-center gap-2">
            <div className="flex w-full items-center justify-between">
              <span className="text-xs text-gray-500">
                {hasTotal ? `${safeProcessed} de ${safeTotal}` : 'Iniciando...'}
              </span>
              <span className="text-xs font-semibold text-gray-900">{percent}%</span>
            </div>
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-neutral-100">
              <div
                className="bg-brand-600 h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>

          {onCancelar && (
            <div className="mt-6">
              <Button variant="neutral-secondary" onClick={onCancelar}>
                Cancelar
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
